<!-- Pricing Modal -->
<app-pricing-modal
  [isOpen]="isPricingModalOpen()"
  [target]="paywallTarget"
  (closed)="closePricingModal()"
  (productSelected)="onPricingProductSelected()"
  (paymentSuccess)="onPaymentSuccess($event)"
></app-pricing-modal>

<!-- Subscription Success Modal -->
<app-subscription-success-modal
  [isOpen]="isSubscriptionSuccessOpen()"
  [productName]="subscribedProductName()"
  (closed)="closeSubscriptionSuccess()"
></app-subscription-success-modal>

@if (simplifiedMode()) {
  <!-- Simplified Mode (Tablet) - No rulers, no zoom, just the slide -->
  <div class="simplified-canvas-container flex flex-col h-full">
    <!-- Slide Area -->
    <div class="flex-1 flex items-center justify-center bg-gray-100 p-4 overflow-hidden">
      <div class="simplified-slide-wrapper w-full max-w-4xl aspect-video bg-white rounded-lg shadow-lg overflow-hidden">
        @if (isLoadingThumbnail()) {
          <div class="w-full h-full flex flex-col justify-center items-center bg-gray-50">
            <lucide-icon [img]="icons.Loader2" class="w-8 h-8 text-brand animate-spin mb-3"></lucide-icon>
            <p class="text-sm text-gray-500">Loading slide...</p>
          </div>
        } @else if (thumbnail()) {
          <img 
            [src]="thumbnail()!.thumbnailUrl" 
            alt="Slide {{ slideNumber() }}"
            class="w-full h-full object-contain"
            loading="lazy"
          />
        } @else {
          <div class="w-full h-full flex items-center justify-center bg-gray-100">
            <span class="text-sm text-gray-400">No preview available</span>
          </div>
        }
      </div>
    </div>

    <!-- Bottom Toolbar (simplified) -->
    <div class="shrink-0 h-10 bg-white border-t border-gray-200 flex items-center justify-center px-3 gap-4">
      <!-- Google Slides Link -->
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <lucide-icon [img]="icons.ExternalLink" class="w-3.5 h-3.5 text-brand shrink-0"></lucide-icon>
        <button 
          (click)="openInGoogleSlides()"
          class="inline-flex items-center gap-1 text-brand hover:text-brand/80 font-medium cursor-pointer hover:underline"
        >
          <span>Open in Google Slides</span>
        </button>
        <span class="text-gray-400">|</span>
        <button 
          (click)="onLeaveFeedback()"
          class="inline-flex items-center gap-1 text-gray-500 hover:text-brand font-medium cursor-pointer hover:underline"
        >
          <span>Leave feedback</span>
          <lucide-icon [img]="icons.MessageSquare" class="w-3 h-3"></lucide-icon>
        </button>
      </div>
    </div>
  </div>
} @else {
  <!-- Full Mode (Desktop) - With rulers and zoom -->
  <div class="slide-canvas-container flex flex-col h-full">
    <!-- Canvas Area with Rulers -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Top Ruler -->
      <div class="shrink-0 flex">
        <!-- Corner -->
        <div class="w-6 h-6 bg-gray-200 border-r border-b border-gray-300"></div>
        <!-- Horizontal Ruler -->
        <div class="flex-1 h-6 bg-gray-200 border-b border-gray-300 overflow-hidden relative">
          <div 
            class="ruler-h absolute bottom-0 h-full"
            [style.width.px]="scaledSlideWidth()"
            [style.left.px]="rulerOffsetX()"
          >
            @for (tick of horizontalTicks(); track tick.position) {
              <div 
                class="absolute bottom-0 flex flex-col items-center"
                [style.left.px]="tick.position"
              >
                @if (tick.label) {
                  <span class="text-[9px] text-gray-500 leading-none mb-0.5">{{ tick.label }}</span>
                }
                <div class="w-px bg-gray-400" [style.height.px]="tick.isMajor ? 8 : 4"></div>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Main canvas with left ruler -->
      <div class="flex-1 flex overflow-hidden">
        <!-- Left Ruler -->
        <div class="shrink-0 w-6 bg-gray-200 border-r border-gray-300 overflow-hidden relative">
          <div 
            class="ruler-v absolute left-0 w-full"
            [style.height.px]="scaledSlideHeight()"
            [style.top.px]="rulerOffsetY()"
          >
            @for (tick of verticalTicks(); track tick.position) {
              <div 
                class="absolute right-0 flex items-center justify-end"
                [style.top.px]="tick.position"
              >
                @if (tick.label) {
                  <span class="text-[9px] text-gray-500 leading-none mr-1 whitespace-nowrap">{{ tick.label }}</span>
                }
                <div class="h-px bg-gray-400" [style.width.px]="tick.isMajor ? 8 : 4"></div>
              </div>
            }
          </div>
        </div>

        <!-- Slide Canvas -->
        <div 
          #canvasContainer
          class="flex-1 bg-gray-100 canvas-scroll"
          (scroll)="onCanvasScroll($event)"
        >
          <div class="canvas-content">
            <!-- Wrapper for proper centering (has the visual scaled size) -->
            <div 
              #slideWrapper
              class="slide-wrapper"
              [style.width.px]="scaledSlideWidth()"
              [style.height.px]="scaledSlideHeight()"
            >
              <!-- The Slide (base size, scaled via transform) -->
              <div
                class="slide-frame bg-white rounded shadow-lg overflow-hidden"
                [style.transform]="'scale(' + zoomLevel() / 100 + ')'"
              >
                @if (isLoadingThumbnail()) {
                  <!-- Loading state -->
                  <div class="slide-content flex flex-col justify-center items-center h-full bg-gray-50">
                    <lucide-icon [img]="icons.Loader2" class="w-8 h-8 text-brand animate-spin mb-3"></lucide-icon>
                    <p class="text-sm text-gray-500">Loading slide...</p>
                  </div>
                } @else {
                  <!-- Slide Thumbnail -->
                  <img 
                    [src]="thumbnail()!.thumbnailUrl" 
                    alt="Slide {{ slideNumber() }}"
                    class="w-full h-full object-contain"
                    loading="lazy"
                  />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Zoom Toolbar -->
    <div class="shrink-0 h-10 bg-white border-t border-gray-200 flex items-center justify-between px-3 gap-2">
      <!-- Google Slides Link -->
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <lucide-icon [img]="icons.ExternalLink" class="w-3.5 h-3.5 text-brand shrink-0"></lucide-icon>
        <button 
          (click)="openInGoogleSlides()"
          class="inline-flex items-center gap-1 text-brand hover:text-brand/80 font-medium cursor-pointer hover:underline"
        >
          <span>Open in Google Slides</span>
        </button>
        <span class="hidden md:inline text-gray-400">|</span>
        <button 
          (click)="onLeaveFeedback()"
          class="hidden md:inline-flex items-center gap-1 text-gray-500 hover:text-brand font-medium cursor-pointer hover:underline"
        >
          <span>Leave feedback</span>
          <lucide-icon [img]="icons.MessageSquare" class="w-3 h-3"></lucide-icon>
        </button>
      </div>

      <!-- Zoom Controls -->
      <div class="flex items-center gap-1">
        <button
          (click)="zoomOut()"
          [disabled]="zoomLevel() <= 25"
          class="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer"
          title="Zoom out"
        >
          <lucide-icon [img]="icons.Minus" class="w-4 h-4"></lucide-icon>
        </button>
        <div class="relative">
          <select
            [ngModel]="zoomLevel()"
            (ngModelChange)="setZoom($event)"
            class="text-sm bg-transparent border border-gray-200 rounded px-2 py-1 cursor-pointer hover:bg-gray-50 min-w-[70px] text-center appearance-none"
          >
            @for (level of displayZoomLevels(); track level) {
              <option [ngValue]="level">{{ level }}%</option>
            }
          </select>
        </div>
        <button
          (click)="zoomIn()"
          [disabled]="zoomLevel() >= 200"
          class="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer"
          title="Zoom in"
        >
          <lucide-icon [img]="icons.Plus" class="w-4 h-4"></lucide-icon>
        </button>
        <button
          (click)="fitToWindow()"
          class="p-1.5 rounded hover:bg-gray-100 transition-colors cursor-pointer ml-1"
          title="Fit to window"
        >
          <lucide-icon [img]="icons.Maximize2" class="w-4 h-4"></lucide-icon>
        </button>
      </div>
    </div>
  </div>
}
